import { logger } from '@/utils/logger';
import { useSocialAuth } from '@/hooks/useSocialAuth';
import { PendingSignupData } from '@/hooks/useSocialLogin';
import { ErrorText, ModalTitle } from '@/styles/Typography';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { GradientButton } from '../button/Button';
import { AgreementSection } from '../common/AgreementSection';
import { Snackbar, useSnackbar } from '../common/Snackbar';
import { Modal } from '../modal/Modal';
import { FormField, FormLabel, FormInput } from '@/styles/Form';

interface SocialSignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  pendingSignupData?: PendingSignupData;
}

export const SocialSignUpModal: React.FC<SocialSignUpModalProps> = ({ isOpen, onClose, onBack, pendingSignupData }) => {
  const [userName, setUsername] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAgreementValid, setIsAgreementValid] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const { completeSocialSignup } = useSocialAuth();

  const handleAgreementChange = (allAgreed: boolean) => {
    setIsAgreementValid(allAgreed);
  };

  // name 유효성 검사 - 상태를 변경하지 않는 순수 함수로 변경
  const validateUsernameValue = (value: string): { isValid: boolean; errorMessage: string } => {
    if (value.length < 2 || value.length > 20) {
      return { isValid: false, errorMessage: 'Name must be between 2 and 20 characters' };
    }
    return { isValid: true, errorMessage: '' };
  };

  // 실제 유효성 검사 (렌더링할 때마다 호출되지 않음)
  const isFormValid = () => {
    const validation = validateUsernameValue(userName);
    return validation.isValid && !error && isAgreementValid;
  };

  // 입력 핸들러 수정
  const handleNameChange = (value: string) => {
    setUsername(value);
    if (value) {
      const validation = validateUsernameValue(value);
      setError(validation.errorMessage);
    } else {
      setError('');
    }
  };

  const handleNameBlur = () => {
    if (userName) {
      const validation = validateUsernameValue(userName);
      setError(validation.errorMessage);
    }
  };

  // Continue 처리
  const handleContinue = async () => {
    if (!isFormValid()) return;
    try {
      setIsLoading(true);

      if (!pendingSignupData?.tempToken || !pendingSignupData?.provider) {
        setError('임시 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      // 구글과 카카오 모두 동일한 API 사용
      const response = await completeSocialSignup(pendingSignupData.tempToken, pendingSignupData.provider, userName);

      if (response.success) {
        showSnackbar('회원가입이 완료되었습니다!', {
          showCheckIcon: true,
          duration: 3000,
        });
        onClose();

        // ✅ 회원가입 완료 후 홈으로 리다이렉션
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        setError(response.error || '회원가입 완료 중 오류가 발생했습니다.');
      }
    } catch (error) {
      logger.error('Social sign up error:', { error });
      setError('Failed to complete sign up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setUsername('');
    setError('');
    setIsAgreementValid(false);

    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setUsername('');
    }
  }, [isOpen]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="medium">
        <ModalContent>
          <ModalTitle style={{ fontSize: '24px', marginBottom: '40px' }}>Finish setting up your account</ModalTitle>

          <FormContainer>
            <InputWrapper>
              <Label>Name</Label>
              <Input
                type="text"
                value={userName}
                onChange={e => handleNameChange(e.target.value)}
                onBlur={handleNameBlur}
                placeholder="Your name"
                maxLength={20}
                state={error ? 'error' : 'default'}
              />
              {error && <ErrorText>{error}</ErrorText>}
            </InputWrapper>

            <AgreementSection onAgreementChange={handleAgreementChange} context="signup" showAgeConfirmation={true} />
          </FormContainer>

          <ButtonGroup>
            <GradientButton
              onClick={handleContinue}
              loading={isLoading}
              disabled={!isFormValid()}
              width="100%"
              height="47px"
            >
              Continue
            </GradientButton>

            <BackButton onClick={handleBack}>Back</BackButton>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        onClose={hideSnackbar}
        showCheckIcon={snackbar.showCheckIcon}
        showCloseIcon={snackbar.showCloseIcon}
        duration={snackbar.duration}
      />
    </>
  );
};

// Styled Components
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 27px 32px;
  width: 100%;
  box-sizing: border-box;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 533px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 48px;
`;

const InputWrapper = styled(FormField)``;

const Label = styled(FormLabel)`
  top: -24px;
`;

const Input = styled(FormInput)``;

const ButtonGroup = styled.div`
  width: 100%;
  max-width: 533px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 21px;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
